import { apiGet, apiPost } from "./axiosClient";
import type {
  Payment,
  RazorpayOrder,
  RazorpayVerifyRequest,
} from "@/types/domain";

/**
 * Bump when list response handling changes so React Query does not reuse a
 * stale cached Spring `Page` object as if it were `Payment[]`.
 */
export const MY_PAYMENTS_QUERY_KEY = ["my-payments", "v2"] as const;

function readString(v: unknown): string | undefined {
  return typeof v === "string" && v.length > 0 ? v : undefined;
}

function readNumber(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }
  return undefined;
}

function coercePayment(raw: unknown): Payment | null {
  if (raw == null || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = readString(o.id);
  const appointmentId = readString(o.appointmentId);
  const amount = readNumber(o.amount) ?? readNumber(o.amountPaise);
  const currency = readString(o.currency) ?? "INR";
  const status = readString(o.status);
  if (!id || !appointmentId || amount == null || !status) return null;

  return {
    id,
    appointmentId,
    amount,
    currency,
    status:
      status === "CREATED" ||
      status === "CAPTURED" ||
      status === "FAILED" ||
      status === "ABANDONED"
        ? status
        : "CREATED",
    razorpayOrderId: readString(o.razorpayOrderId),
    razorpayPaymentId: readString(o.razorpayPaymentId),
    createdAt: readString(o.createdAt),
  };
}

export function normalizePaymentList(data: unknown, depth = 0): Payment[] {
  if (data == null || depth > 4) return [];
  if (Array.isArray(data)) {
    return data
      .map((item) => coercePayment(item))
      .filter((p): p is Payment => p != null);
  }
  if (typeof data !== "object") return [];

  const o = data as Record<string, unknown>;
  if (Array.isArray(o.content)) {
    return o.content
      .map((item) => coercePayment(item))
      .filter((p): p is Payment => p != null);
  }
  if ("data" in o) {
    return normalizePaymentList(o.data, depth + 1);
  }
  return [];
}

export const createOrder = (appointmentId: string) =>
  apiPost<RazorpayOrder>("/api/payments/orders", { appointmentId });

export const verifyPayment = (body: RazorpayVerifyRequest) =>
  apiPost<Payment>("/api/payments/verify", body);

export const listMyPayments = async () => {
  const data = await apiGet<unknown>("/api/payments/mine");
  return normalizePaymentList(data);
};
